import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  get,
  post,
  put,
  del,
  patch,
  postMultipart,
  patchMultipart,
  putMultipart,
} from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { mapWorkItemsListResponse } from "../../Pages/Projects/projectMappers";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { buildWorkItemTimeLogsQueryParams } from "../../utils/workItemTimeLogsQueryParams";
import {
  projectsListEndpoint,
  buildProjectsListQuery,
  resolveProjectIdEndpoint,
  buildGlobalWorkItemsQuery,
  buildMyWorkItemsQuery,
  buildProjectWorkItemsQuery,
  WORK_ITEMS_GLOBAL_OR_MY_LIST_SLOT_KEY,
  buildWorkItemAttachmentsFormData,
  buildCreateWorkItemWithAttachmentsFormData,
  buildWorkItemWithAttachmentsFormData,
  buildAddCommentBody,
  buildAddCommentWithAttachmentsFormData,
  buildAddTimeLogBody,
} from "./projectManagementThunkUtils";

export const fetchProjectsData = createAsyncThunk(
  "projects/fetchData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const getAll = params.getAll === true;
      const response = await get(
        projectsListEndpoint(getAll),
        buildProjectsListQuery(params, getAll)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch projects data"));
    }
  },
  {
    condition: (params, { getState }) => {
      if (params?.getAll !== true || params?.force) return true;
      const { allListLoaded, allListData } = getState().projects;
      return !(allListLoaded && allListData != null);
    },
  }
);

export const fetchProjectById = createAsyncThunk(
  "projects/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const endpoint = resolveProjectIdEndpoint(API_ENDPOINTS.PROJECTS.GET_BY_ID, id);
      const response = await get(endpoint);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project data"));
    }
  }
);

/** GET /projects/:projectId/tags — { projectId, clientTags[], vendorTags[] } (system list rows) */
export const fetchProjectTags = createAsyncThunk(
  "projects/fetchTags",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.PROJECTS.GET_TAGS(projectId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project tags"));
    }
  }
);

export const fetchProjectDashboard = createAsyncThunk(
  "projects/fetchDashboard",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.PROJECTS.GET_DASHBOARD(projectId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project dashboard"));
    }
  }
);

/**
 * Accepts either a plain project body (backward compatible, no documents) or the same body
 * with a `files` key — when files are present, sends multipart with fields JSON-stringified
 * under `data` and each file under `files`, per the documents upload contract.
 */
export const createProject = createAsyncThunk(
  "projects/create",
  async (arg, { rejectWithValue }) => {
    try {
      const { files, ...projectData } = arg || {};
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildWorkItemWithAttachmentsFormData(projectData, fileList);
        const response = await postMultipart(API_ENDPOINTS.PROJECTS.CREATE, formData);
        return response.data;
      }
      const response = await post(API_ENDPOINTS.PROJECTS.CREATE, projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create project"));
    }
  }
);

export const createWorkItem = createAsyncThunk(
  "projects/createWorkItem",
  async ({ projectId, body, files, apiOptions }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.CREATE_WORK_ITEM(projectId);
      const axiosConfig = {
        ...(apiOptions?.skipToast ? { skipToast: true } : {}),
        ...(apiOptions?.suppressGlobalErrorModal ? { suppressGlobalErrorModal: true } : {}),
      };
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildCreateWorkItemWithAttachmentsFormData(body, fileList);
        const response = await postMultipart(endpoint, formData, {}, axiosConfig);
        return response.data;
      }
      const response = await post(endpoint, body, {}, "json", axiosConfig);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create work item"));
    }
  }
);

export const uploadWorkItemAttachments = createAsyncThunk(
  "projects/uploadWorkItemAttachments",
  async ({ projectId, workItemId, files = [] }, { rejectWithValue }) => {
    try {
      if (!projectId || !workItemId || !Array.isArray(files) || files.length === 0) {
        return [];
      }
      const response = await postMultipart(
        API_ENDPOINTS.PROJECTS.UPLOAD_WORK_ITEM_ATTACHMENTS(projectId, workItemId),
        buildWorkItemAttachmentsFormData(files)
      );
      return response?.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to upload work item attachments"));
    }
  }
);

export const listWorkItemAttachments = createAsyncThunk(
  "projects/listWorkItemAttachments",
  async ({ projectId, workItemId }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.PROJECTS.LIST_WORK_ITEM_ATTACHMENTS(projectId, workItemId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to list work item attachments"));
    }
  }
);

export const getWorkItemAttachmentDownloadUrl = createAsyncThunk(
  "projects/getWorkItemAttachmentDownloadUrl",
  async ({ projectId, workItemId, attachmentId, expiresIn = 3600 }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_ATTACHMENT_URL(projectId, workItemId, attachmentId),
        { expiresIn }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to get attachment download URL"));
    }
  }
);

export const deleteWorkItemAttachment = createAsyncThunk(
  "projects/deleteWorkItemAttachment",
  async ({ projectId, workItemId, attachmentId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.PROJECTS.DELETE_WORK_ITEM_ATTACHMENT(projectId, workItemId, attachmentId),
        {},
        { skipToast: true }
      );
      return String(attachmentId);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete attachment"));
    }
  }
);

export const fetchAllWorkItems = createAsyncThunk(
  "issues/fetchAllWorkItems",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_ALL,
        buildGlobalWorkItemsQuery(params),
        {},
        { requestKey: WORK_ITEMS_GLOBAL_OR_MY_LIST_SLOT_KEY }
      );
      return mapWorkItemsListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work items"));
    }
  }
);

/** Global Issues Kanban — GET /work-items/all (same query shape as GET /work-items). */
export const fetchAllWorkItemsForGlobalKanban = createAsyncThunk(
  "issues/fetchAllWorkItemsForGlobalKanban",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_ALL_GLOBAL_KANBAN,
        buildGlobalWorkItemsQuery(params),
        {},
        { requestKey: WORK_ITEMS_GLOBAL_OR_MY_LIST_SLOT_KEY }
      );
      return mapWorkItemsListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work items"));
    }
  }
);

export const fetchMyWorkItems = createAsyncThunk(
  "issues/fetchMyWorkItems",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_MY,
        buildMyWorkItemsQuery(params),
        {},
        { requestKey: WORK_ITEMS_GLOBAL_OR_MY_LIST_SLOT_KEY }
      );
      return mapWorkItemsListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch my work items"));
    }
  }
);

export const fetchWorkItemById = createAsyncThunk(
  "projects/fetchWorkItemById",
  async ({ projectId, workItemId }, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.PROJECTS.GET_WORK_ITEM(projectId, workItemId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work item"));
    }
  }
);

export const updateWorkItem = createAsyncThunk(
  "projects/updateWorkItem",
  async ({ projectId, workItemId, body, files, apiOptions }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.UPDATE_WORK_ITEM(projectId, workItemId);
      const axiosConfig = {
        ...(apiOptions?.skipToast ? { skipToast: true } : {}),
        ...(apiOptions?.suppressGlobalErrorModal ? { suppressGlobalErrorModal: true } : {}),
      };
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildWorkItemWithAttachmentsFormData(body, fileList);
        const response = await patchMultipart(endpoint, formData, {}, axiosConfig);
        return response.data;
      }
      const response = await patch(
        endpoint,
        body,
        {},
        { "Content-Type": "application/json" },
        axiosConfig
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update work item"));
    }
  }
);

export const deleteWorkItem = createAsyncThunk(
  "projects/deleteWorkItem",
  async ({ projectId, workItemId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.PROJECTS.DELETE_WORK_ITEM(projectId, workItemId),
        {},
        { skipToast: true }
      );
      return workItemId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete work item"));
    }
  }
);

export const fetchWorkItemsByProjectId = createAsyncThunk(
  "projects/fetchWorkItemsByProjectId",
  async (arg, { rejectWithValue }) => {
    const { projectId, body = {} } = arg;
    try {
      const response = await get(
        API_ENDPOINTS.WORK_ITEMS.GET_BY_PROJECT(projectId),
        buildProjectWorkItemsQuery(body)
      );
      return mapWorkItemsListResponse(response.data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project work items"));
    }
  }
);

export const fetchWorkItemComments = createAsyncThunk(
  "projects/fetchWorkItemComments",
  async ({ projectId, workItemId, page = 1, limit = 5 }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_COMMENTS(projectId, workItemId),
        {
          page,
          limit,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch comments"));
    }
  }
);

export const fetchWorkItemHistory = createAsyncThunk(
  "projects/fetchWorkItemHistory",
  async ({ projectId, workItemId }, { rejectWithValue }) => {
    try {
      const response = await get(
        API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_HISTORY(projectId, workItemId)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work item history"));
    }
  }
);

export const addWorkItemComment = createAsyncThunk(
  "projects/addWorkItemComment",
  async ({ projectId, workItemId, comment, mentionedUserIds, files }, { rejectWithValue }) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.ADD_WORK_ITEM_COMMENT(projectId, workItemId);
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildAddCommentWithAttachmentsFormData(
          comment,
          mentionedUserIds,
          fileList
        );
        const response = await postMultipart(endpoint, formData);
        return response.data;
      }
      const response = await post(endpoint, buildAddCommentBody(comment, mentionedUserIds));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to add comment"));
    }
  }
);

export const updateWorkItemComment = createAsyncThunk(
  "projects/updateWorkItemComment",
  async (
    { projectId, workItemId, commentId, comment, mentionedUserIds, files },
    { rejectWithValue }
  ) => {
    try {
      const endpoint = API_ENDPOINTS.PROJECTS.UPDATE_WORK_ITEM_COMMENT(
        projectId,
        workItemId,
        commentId
      );
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildAddCommentWithAttachmentsFormData(
          comment,
          mentionedUserIds,
          fileList
        );
        const response = await patchMultipart(endpoint, formData, {}, { skipToast: true });
        return response.data;
      }
      const response = await patch(
        endpoint,
        buildAddCommentBody(comment, mentionedUserIds),
        {},
        {},
        { skipToast: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update comment"));
    }
  }
);

export const getWorkItemCommentAttachmentDownloadUrl = createAsyncThunk(
  "projects/getWorkItemCommentAttachmentDownloadUrl",
  async (
    { projectId, workItemId, commentId, attachmentId, expiresIn = 3600 },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_COMMENT_ATTACHMENT_URL(
          projectId,
          workItemId,
          commentId,
          attachmentId
        ),
        { expiresIn }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to get comment attachment download URL")
      );
    }
  }
);

export const deleteWorkItemCommentAttachment = createAsyncThunk(
  "projects/deleteWorkItemCommentAttachment",
  async ({ projectId, workItemId, commentId, attachmentId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.PROJECTS.DELETE_WORK_ITEM_COMMENT_ATTACHMENT(
          projectId,
          workItemId,
          commentId,
          attachmentId
        ),
        {},
        { skipToast: true }
      );
      return String(attachmentId);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete comment attachment"));
    }
  }
);

export const deleteWorkItemComment = createAsyncThunk(
  "projects/deleteWorkItemComment",
  async ({ projectId, workItemId, commentId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.PROJECTS.DELETE_WORK_ITEM_COMMENT(projectId, workItemId, commentId),
        {},
        { skipToast: true }
      );
      return commentId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete comment"));
    }
  }
);

export const addWorkItemTimeLog = createAsyncThunk(
  "projects/addWorkItemTimeLog",
  async ({ projectId, workItemId, minutesLogged, logDate, comment }, { rejectWithValue }) => {
    try {
      const response = await post(
        API_ENDPOINTS.PROJECTS.ADD_WORK_ITEM_TIME_LOG(projectId, workItemId),
        buildAddTimeLogBody({ minutesLogged, logDate, comment }),
        {},
        "json",
        { skipToast: true, suppressGlobalErrorModal: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to log time"));
    }
  }
);

export const fetchWorkItemTimeLogs = createAsyncThunk(
  "projects/fetchWorkItemTimeLogs",
  async (
    {
      projectId,
      workItemId,
      scope,
      dateFrom,
      dateTo,
      page,
      limit,
      sortBy,
      sortOrder,
      requestScope = "default",
    },
    { rejectWithValue }
  ) => {
    try {
      const url = API_ENDPOINTS.PROJECTS.GET_WORK_ITEM_TIME_LOGS(projectId, workItemId);
      const params = buildWorkItemTimeLogsQueryParams({
        scope,
        dateFrom,
        dateTo,
        page,
        limit,
        sortBy,
        sortOrder,
      });
      // Separate cancel/dedupe keys so IssueDetail (sidebar totals) and ActivityTabs (list) do not abort each other.
      const response = await get(
        url,
        params,
        {},
        {
          requestKey: `work-item-time-logs:${String(projectId)}:${String(workItemId)}:${String(requestScope)}`,
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch work item time logs"));
    }
  }
);

/** `files` (optional) triggers multipart, same `data` + `files` contract as {@link createProject}. */
export const updateProject = createAsyncThunk(
  "projects/update",
  async ({ id, projectData, files }, { rejectWithValue }) => {
    try {
      const endpoint = resolveProjectIdEndpoint(API_ENDPOINTS.PROJECTS.UPDATE, id);
      const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
      if (fileList.length > 0) {
        const formData = buildWorkItemWithAttachmentsFormData(projectData, fileList);
        const response = await putMultipart(endpoint, formData);
        return response.data;
      }
      const response = await put(endpoint, projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update project"));
    }
  }
);

/** Replace project client/vendor/language tag assignments — PUT /projects/:id/tags */
export const updateProjectTags = createAsyncThunk(
  "projects/updateTags",
  async (
    { projectId, clientTagIds = [], vendorTagIds = [], languageIds = [] },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_ENDPOINTS.PROJECTS.PUT_TAGS(projectId), {
        clientTagIds: Array.isArray(clientTagIds) ? clientTagIds.map(String) : [],
        vendorTagIds: Array.isArray(vendorTagIds) ? vendorTagIds.map(String) : [],
        languageIds: Array.isArray(languageIds) ? languageIds.map(String) : [],
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update project tags"));
    }
  }
);

/** GET /projects/:id/attachments — list uploaded documents. */
export const fetchProjectAttachments = createAsyncThunk(
  "projects/fetchAttachments",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await get(API_ENDPOINTS.PROJECTS.GET_ATTACHMENTS(projectId));
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch project attachments"));
    }
  }
);

/** GET /projects/:id/attachments/:attachmentId/url — signed download link. */
export const getProjectAttachmentDownloadUrl = createAsyncThunk(
  "projects/getAttachmentUrl",
  async ({ projectId, attachmentId, expiresIn }, { rejectWithValue }) => {
    try {
      const params = expiresIn != null ? { expiresIn } : {};
      const response = await get(
        API_ENDPOINTS.PROJECTS.GET_ATTACHMENT_URL(projectId, attachmentId),
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to get download URL"));
    }
  }
);

/** DELETE /projects/:id/attachments/:attachmentId */
export const deleteProjectAttachment = createAsyncThunk(
  "projects/deleteAttachment",
  async ({ projectId, attachmentId }, { rejectWithValue }) => {
    try {
      await del(
        API_ENDPOINTS.PROJECTS.DELETE_ATTACHMENT(projectId, attachmentId),
        {},
        { skipToast: true }
      );
      return attachmentId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete attachment"));
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/delete",
  async (id, { rejectWithValue }) => {
    try {
      await del(resolveProjectIdEndpoint(API_ENDPOINTS.PROJECTS.DELETE, id));
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete project"));
    }
  }
);
